import { createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../../../configureStore";
import { API } from "../../../config";
import axios from "axios";
import { CerealType, DataTableColumns } from "./DataTableTypes";

import {
  addHours,
  addHoursSameTime,
  setZeroHrs,
} from "../dashboard/dashboardAsyncActions";
import { setFiltersSelected } from "../../slices/dataTable/dataTableFilters";
import { setPastActionIds } from "../../slices/data/dataSampleView";
import { normalizeText } from "../../../components/helpers/helpersSearchs";
import { GridSortModel } from "@mui/x-data-grid-pro";
import {
  setAppliedView,
  setRunAppliedView,
} from "../../slices/dataTable/dataViews";
import {
  setFilterModel,
  setLoadForFirstTime,
} from "../../slices/dataTable/data";

const pagesToPreFetch = (totalPages: number) => {
  let array = [] as number[];
  for (let i = 0; i < totalPages; i++) {
    array.push(i);
  }

  return array;
};

const buildSkipParam = (page: number, rowOptions: number): string => {
  return page === 0 ? "" : `$skip=${page * rowOptions}`;
};

const buildDateParams = (dataDateActivity: Date[]) => {
  const fromDate = `createdAt[$gt]=${addHours(
    setZeroHrs(dataDateActivity[0]),
  ).toISOString()}`;

  const toDate = `createdAt[$lt]=${addHoursSameTime(
    setZeroHrs(dataDateActivity[1]),
  ).toISOString()}`;

  return { fromDate, toDate };
};

export const buildBaseQueryParams = (
  page: number,
  rowOptions: number,
  dataDateActivity: Date[],
) => {
  const { fromDate, toDate } = buildDateParams(dataDateActivity);

  return [
    buildSkipParam(page, rowOptions),
    `$limit=${rowOptions}`,
    fromDate,
    toDate,
  ]
    .filter(Boolean)
    .join("&");
};

const handleCropTextIdField = (filter: any, species: any[], index: number) => {
  const searchValue = normalizeText(filter.value);
  let foundSpecie = species.find(
    (specie) => specie.name && normalizeText(specie.name) === searchValue,
  );

  let value = filter.value;
  if (foundSpecie && foundSpecie.textId) {
    value = foundSpecie.textId;
  }

  if (filter.operator === "$ilike") {
    value = `%${value}%`;
  }

  return `$and[${index}][${filter.field}][${
    filter.operator
  }]=${encodeURIComponent(value)}`;
};

const handleScenarioNameTextIdField = (
  filter: any,
  actions: any[],
  index: number,
) => {
  const searchValue = normalizeText(filter.value);
  let foundAction = actions.find(
    (action) => action.name && normalizeText(action.name) === searchValue,
  );

  let value = filter.value;
  if (foundAction && foundAction.userFacingNameTextId) {
    value = foundAction.userFacingNameTextId;
  }

  if (filter.operator === "$ilike") {
    value = `%${value}%`;
  }

  return `$and[${index}][${filter.field}][${
    filter.operator
  }]=${encodeURIComponent(value)}`;
};

export const handleSortingField = (sortModel: GridSortModel) => {
  if (sortModel.length === 0) {
    return "";
  }

  const sortParams = sortModel
    .map((sort) => `&$sort[${sort.field}]=${sort.sort === "asc" ? "-1" : "1"}`)
    .join("&");
  return sortParams;
};

const handleDataColumnField = (filter: any, index: number) => {
  const values = filter.value
    .split(",")
    .map((v: string) => v.trim())
    .filter((v: string) => v !== "");

  if (values.length > 0) {
    const orConditions = values.map((val: string) => {
      let processedValue = val;
      if (filter.operator === "$ilike") {
        processedValue = `%${val}%`;
      }

      return `$and[${index}][${filter.field}][${
        filter.operator
      }]=${encodeURIComponent(processedValue)}`;
    });
    return orConditions.join("&");
  }
  return "";
};

const handleExportedMeasureField = (filter: any) => {
  let value = filter.value;
  return `aggExportedPredictions[${filter.field}][${filter.operator}]=${encodeURIComponent(value)}`;
};

export const buildFilterParams = (
  filterModel: any,
  species: any[],
  actions: any[],
) => {
  const filters = filterModel.items;

  const queryParams = filters
    .map((filter: any, i: number) => {
      if (filter.field === "cropTextId") {
        return handleCropTextIdField(filter, species, i);
      }

      if (filter.field === "scenarioNameTextId") {
        return handleScenarioNameTextIdField(filter, actions, i);
      }

      if (filter.columnType === "exported-measure") {
        return handleExportedMeasureField(filter);
      }

      if (filter.columnType === "data") {
        return handleDataColumnField(filter, i);
      }

      if (filter.operator && filter.value) {
        let value = filter.value;
        if (filter.operator === "$ilike") {
          value = `%${value}%`;
        }
        return `$and[${i}][${filter.field}][${
          filter.operator
        }]=${encodeURIComponent(value)}`;
      }

      return "";
    })
    .filter(Boolean);

  return queryParams.join("&");
};

const buildApiUrl = (
  baseParams: string,
  filterParams: string,
  sortParams: string,
  userFacingDeletion: string,
) => {
  return `${API}/portal/managed-data-table?${userFacingDeletion}&${baseParams}${filterParams}${sortParams}`;
};

const buildPrefetchUrl = (
  baseParams: string,
  filterParams: string,
  selectParam: string = "",
  userFacingDeletion: string,
) => {
  const selectQuery = selectParam ? `&${selectParam}` : "";
  return `${API}/portal/managed-data-table?${userFacingDeletion}&${baseParams}${filterParams}${selectQuery}`;
};

const executePrefetchRequests = async (
  pagesToPreFetch: number[],
  rowOptions: number,
  dataDateActivity: Date[],
  filterParams: string,
  locale: string,
  userToken: string,
  store: any,
  userFacingDeletion: string,
) => {
  if (pagesToPreFetch.length === 0) {
    return;
  }

  const { fromDate, toDate } = buildDateParams(dataDateActivity);

  const prefetchPromises = pagesToPreFetch.map(async (page) => {
    const queryParamsPrefetch = [
      `$skip=${page * rowOptions}`,
      `$limit=${rowOptions}`,
      fromDate,
      toDate,
    ]
      .filter(Boolean)
      .join("&");

    try {
      const subResult = await axios.get(
        buildPrefetchUrl(
          queryParamsPrefetch,
          filterParams,
          "$select=pastActionId",
          userFacingDeletion,
        ),
        {
          headers: {
            "Accept-Language": locale,
            Authorization: `Bearer ${userToken}`,
          },
          timeout: 5000,
        },
      );

      return subResult.data.data?.map((item: any) => item.pastActionId) || [];
    } catch (error) {
      return error;
    }
  });

  try {
    const preFetchResults = await Promise.all(prefetchPromises);
    const preFetchedData = preFetchResults.flat();

    if (preFetchedData.length > 0) {
      store.dispatch(setPastActionIds(preFetchedData));
    }
  } catch (error) {
    console.warn("Prefetch batch failed:", error);
  }
};

export const createCerealDictionary = async (
  cerealInformation: CerealType[],
  store: any,
): Promise<Record<number, CerealType>> => {
  if (cerealInformation.length > 0) {
    return cerealInformation.reduce(
      (acc, cereal) => {
        acc[cereal.textId.id] = cereal;
        return acc;
      },
      {} as Record<number, CerealType>,
    );
  } else {
    const result = await store.dispatch(fetchCerealTypeDataTableDictionary());
    const cerealData = result.payload as CerealType[];
    return cerealData.reduce(
      (acc, cereal) => {
        acc[cereal.textId.id] = cereal;
        return acc;
      },
      {} as Record<number, CerealType>,
    );
  }
};

export const fetchDataColumns = createAsyncThunk(
  "SharedDataReducer/fetchDataColumns",
  async (_, store) => {
    const state = store.getState() as RootState;
    const { user } = state.Authentication;
    const { locale } = state.LayoutReducer;
    const extraColumns = [
      {
        name: "id",
        hidden: false,
        hideable: false,
        localizedName: locale,
        columnType: "data",
        useShortResult: false,
        alwaysHide: false,
        foregroundColor: "#000000",
        backgroundColor: "#FFFFFF",
        availableFilters: [""],
      },
    ];

    try {
      const result = await axios.get<DataTableColumns[]>(
        `${API}/portal/data-columns`,
        {
          headers: {
            "Accept-Language": locale,
            Authorization: `Bearer ${user!.token}`,
          },
        },
      );

      const response = result.data;

      const allColumns = extraColumns.concat(response);

      const filterColumns = allColumns.filter(
        (column) =>
          column.name !== "geoPointId" && column.name !== "cerealTypeId",
      );

      const columns = filterColumns.map((columns) => ({
        field: columns.name,
        headerName: columns.localizedName
          ? columns.localizedName
          : columns.name,
        disableColumnMenu: true,
        show: columns.hidden === false,
        description: "",
        availableFilters: columns.availableFilters
          ? columns.availableFilters
          : [],
        sorteable: false,
        width: 200,
        operators: [],
        static: false,
        columnType: columns.columnType,
        alwaysHide: columns.alwaysHide ?? false,
        useShortResult: columns.useShortResult ?? false,
        foregroundColor: columns.foregroundColor ?? "#000000",
        backgroundColor: columns.backgroundColor ?? "#FFFFFF",
      }));

      const filterAlwaysHideColumns = columns?.filter((c) => !c.alwaysHide);

      return filterAlwaysHideColumns;
    } catch (err) {
      store.rejectWithValue(err);
    }
  },
);

export const fetchCerealTypeDataTableDictionary = createAsyncThunk(
  "SharedDataReducer/fetchCerealTypeDataTable",
  async (_, store) => {
    const state = store.getState() as RootState;
    const { user } = state.Authentication;
    const { locale } = state.LayoutReducer;
    const { cerealInformation } = state.DataTableReducer;

    if (cerealInformation.length > 0) {
      return cerealInformation;
    }
    try {
      const response = await axios.get(`${API}/core/cereal-type`, {
        headers: {
          "Accept-Language": locale,
          Authorization: `Bearer ${user!.token}`,
        },
      });

      return response.data.data as CerealType[];
    } catch (error: any) {
      return error;
    }
  },
);

export const dataTableActionNewFilter = createAsyncThunk(
  "SharedDataReducer/dataTableActionNewFilter",
  async (_, store) => {
    const state = store.getState() as RootState;
    const { user } = state.Authentication;
    const { locale } = state.LayoutReducer;
    const { runAppliedView } = state.DataViewsReducer;
    const { dataDateActivity, dataSBFilterValues, speciesList } =
      state.DataFiltersReducer;

    const { filterModel, cerealInformation, sortModel } =
      state.DataTableReducer;
    const { DataTableOptions } = state.SharedDataReducer;

    const actions = dataSBFilterValues.actions;
    const species = speciesList;

    const TotalFilters = filterModel.items.filter(function (element) {
      return element.value !== "";
    });

    const managingUserFacingDeletion = filterModel.items.filter(
      (item) => item.field === "userFacingDeletion",
    )[0];

    const userFacingDeletionParam = () => {
      if (
        managingUserFacingDeletion &&
        managingUserFacingDeletion.value !== null
      ) {
        return `userFacingDeletion=${managingUserFacingDeletion.value === "true"}`;
      } else {
        return "";
      }
    };
    const filterParams =
      TotalFilters.length > 0
        ? `&${buildFilterParams(filterModel, species, actions)}`
        : "";

    const sortParams = handleSortingField(sortModel);

    const queryParams = buildBaseQueryParams(
      DataTableOptions.page,
      DataTableOptions.rowOptions,
      dataDateActivity,
    );

    store.dispatch(setFiltersSelected(TotalFilters.length));
    try {
      const result = await axios.get(
        buildApiUrl(
          queryParams,
          filterParams,
          sortParams,
          userFacingDeletionParam(),
        ),
        {
          headers: {
            "Accept-Language": locale,
            Authorization: `Bearer ${user!.token}`,
          },
        },
      );

      const cerealDictionary = await createCerealDictionary(
        cerealInformation,
        store,
      );

      const currentPageData = result.data.data;

      const newData = currentPageData.map((data) => ({
        ...data,
        cropInfoColor: cerealDictionary[data.cropTextId.id] || null,
      }));

      const pagesToPreFetchValues = pagesToPreFetch(DataTableOptions.page + 2);
      await executePrefetchRequests(
        pagesToPreFetchValues,
        DataTableOptions.rowOptions,
        dataDateActivity,
        filterParams,
        locale,
        user!.token,
        store,
        userFacingDeletionParam(),
      );

      const response = {
        data: newData,
        total: result.data.total,
        limit: result.data.limit,
      };

      if (runAppliedView) {
        store.dispatch(setAppliedView(true));
        store.dispatch(setRunAppliedView(false));
      }
      store.dispatch(setLoadForFirstTime(false));
      return response as { data: any[]; total: number; limit: number };
    } catch (error) {
      return error;
    }
  },
);

export const applyUserFacingDeletionFilterDataTable = createAsyncThunk(
  "DataReducer/applyUserFacingDeletionFilter",
  async (_, store) => {
    const { dispatch } = store;

    dispatch(
      setFilterModel({
        items: [],
      }),
    );

    try {
      dispatch(
        setFilterModel({
          items: [
            {
              field: "computedStatusId",
              operator: "$ne",
              value: "141",
              column: "computedStatusId",
              realOperator: "$ne",
              columnType: "data",
            },
          ],
        }),
      );
    } catch (error) {
      return error;
    }
  },
);
